import moment from 'moment';
import { LitElement } from '~/lib/bn-lit-element';
import { css, svg, renderIf } from '../../utility/lit-entity';
import { DeviceMonitoringChartLayerMixin } from './device-monitoring-chart-layer-mixin';
import '../../common/bn-icon';
import './device-events';

class DeviceMonitoringChartLayerAreas extends DeviceMonitoringChartLayerMixin(LitElement) {
  static get styles() {
    return [...super.styles, css`
      :host {
        --dashed-stroke: var(--gray-10);
        pointer-events: all;
      }
      .item .visual {
        opacity: .6;
      }
      .item.floating-event .visual {
        fill: var(--yellow-20);
      }
      .no-data-banner {
        margin: 4em 4em 0 4em;
        text-align: center;
        padding: 1em;
      }
      .select-box {
        pointer-events: all;
        fill: var(--blue-40);
        stroke: var(--blue-60);
        stroke-width: 2px;
        opacity: 0;
      }
      .select-box.hovered {
        opacity: 1;
      }
      .select-box.selected {
        opacity: 1;
        fill: var(--blue-30);
        stroke: var(--blue-50);
      }

      /* Area Styles */
      .area.no-report .colour {
        fill: var(--blue-50);
      }
      .area.no-report:hover .colour {
        fill: var(--blue-60);
      }
      .area.no-report .pattern {
        opacity: 0.15;
        fill: url(#dashed);
      }
      .area.no-report .separator {
        stroke-width: 1px;
        stroke: var(--dashed-stroke);
        opacity: 0.2;
      }
      .area.valid .visual {
        opacity: 1;
      }
      .area.valid .colour {
        fill: var(--gray-10);
      }
      .area.valid .pattern {
        fill: transparent;
      }
      .area text {
        font-size: .75em;
      }

      /* General / Util Styles */
      .item:hover .visual {
        opacity: 1;
      }
    `];
  }

  renderLayerSVG() {
    this._parseData();

    return svg`
      ${this.getDashedRightPattern({ id: 'dashed', fill: 'transparent', stroke: 'var(--dashed-stroke)' })}
      <g>${this._renderNoReportAreas(this.noReportAreas)}</g>
      <g>${this._renderValidAreas(this.validAreas)}</g>
      <g>${this._renderSelectBoxes(this.events)}</g>
    `;
  }

  _renderSelectBoxes(events) {
    if (!events || events.length === 0) {
      return svg``;
    }

    return events.map((current) => {
      const scaledDate = this.xScale(current.date);
      const selectionClasses = this.getSelectableClassList(current).join(' ');
      const highlightX = scaledDate - (this.levelWidthPadded / 2);

      return svg`
        <g>
          <rect
            x="${highlightX}"
            y="${0}"
            height="${this.barHeightPadded}"
            width="${this.levelWidthPadded}"
            class="select-box ${selectionClasses}"
            @mouseenter="${() => this._onHoverEvent(current)}"
            @click="${() => this._onClickEvent(current)}"
          ></rect>
        </g>
      `;
    }).filter((svg) => svg);
  }

  _onHoverEvent(hovering) {
    this.dateHovering = hovering.rawDate;
    this.dispatchEvent(new CustomEvent('hoverevent', { detail: { date: hovering.rawDate }, bubbles: true, composed: true }));
  }

  _onClickEvent(clicked) {
    this.dispatchEvent(new CustomEvent('clickevent', { detail: { date: clicked.rawDate }, bubbles: true, composed: true }));
  }

  _renderValidAreas(validAreas) {
    if (!validAreas || validAreas.length === 0) {
      return svg``;
    }

    return validAreas.map((area) => {
      const areaWidth = this.xScale(area.startDate) - this.xScale(area.endDate) - this.levelWidthPadded;
      const x = this.xScale(area.endDate) + (this.levelWidthPadded / 2);
      const classes = ['item', 'area', area.type];

      return svg`
        <g class="${classes.join(' ')}">
          <rect
            x="${x}"
            y="${0}"
            height="${this.barHeightPadded}"
            width="${areaWidth}"
            class="visual colour"
            stroke="black"
            stroke-width="1px"
            stroke-dasharray="${areaWidth} ${this.barHeightPadded}"
          ></rect>
          <rect
            x="${x}"
            y="${0}"
            height="${this.barHeightPadded}"
            width="${areaWidth}"
            stroke="grey"
            stroke-width="1px"
            fill="transparent"
            class="no-pointer"
            stroke-dasharray="0 ${areaWidth} ${this.barHeightPadded} 0"
          ></rect>
        </g>
      `;
    });
  }

  _renderNoReportAreas(noReportAreas) {
    if (!noReportAreas || noReportAreas.length === 0) {
      return svg``;
    }

    const { INSET_SCALE, HOURS_PER_READING } = this.constants();
    const height = this.barHeightPadded / INSET_SCALE;
    const y = (this.barHeightPadded / 2) - height / 2;
    const textY = y + height + 12;

    return noReportAreas.map((area) => {
      const areaWidth = this.xScale(area.startDate) - this.xScale(area.endDate) - this.levelWidthPadded;
      const x = this.xScale(area.endDate) + (this.levelWidthPadded / 2);
      const textX = x + (areaWidth / 2);
      const hoursDiff = Math.round(area.secondsDiff / (60 * 60));
      const hoursChunks = Math.round(hoursDiff / HOURS_PER_READING) + 2;
      const classes = ['item', 'area', area.type];

      const lines = [];
      for (let i = 1; i < hoursChunks; i++) {
        const lineX = x + (this.levelWidthPadded * i);
        lines.push(svg`
          <line class="separator no-pointer" x1="${lineX}" x2="${lineX}" y1="${y}" y2="${y + height}"></line>
        `);
      }

      return svg`
        <g class="${classes.join(' ')}">
          <rect
            class="colour"
            x="${x}"
            y="${y}"
            height="${height}"
            width="${areaWidth}"
            stroke="black"
            stroke-width="1px"
            stroke-dasharray="${areaWidth} ${height}"
            @mouseenter="${() => this._onHoverArea(area)}"
          ></rect>
          <rect class="pattern no-pointer" x="${x}" y="${y}" height="${height}" width="${areaWidth}"></rect>
          ${renderIf(this.levelWidthPadded > 10, () => lines)}
          ${renderIf(areaWidth > 40, () => svg`
            <text text-anchor="middle" x="${textX}" y="${textY}">${hoursDiff}h</text>
          `)}
        </g>
      `;
    });
  }

  _onHoverArea(hovering) {
    this.hovering = hovering;
    this.dispatchEvent(new CustomEvent('hoverarea', { detail: { area: hovering }, bubbles: true, composed: true }));
  }

  _onMouseOut() {
    this.hovering = undefined;
    this.dispatchEvent(new CustomEvent('hoverarea', { detail: {}, bubbles: true, composed: true }));
  }

  _pushArea({ areaArray, type, dateStart, dateEnd }) {
    // Don't render negative width areas
    if (dateStart.diff(dateEnd, 'seconds') < 0) {
      return;
    }

    // Don't render empty areas at the start/end of the chart
    if (dateStart.diff(moment(this.start), 'seconds') === 0 || dateEnd.diff(moment(this.end), 'seconds') === 0) {
      return;
    }

    const { REPORT_CADENCE_S } = this.constants();
    const endDate = moment(dateEnd).subtract(REPORT_CADENCE_S, 'seconds');
    const startDate = moment(dateStart).add(REPORT_CADENCE_S, 'seconds');
    // Subtract 4 hours to give an easier to understand time difference
    const secondsDiff = startDate.diff(endDate, 'seconds') - (REPORT_CADENCE_S * 3);

    areaArray.push({
      type,
      secondsDiff,
      startDate: startDate,
      endDate,
    });
  }

  _parseData() {
    if (!this.events || this.events.length === 0) {
      return;
    }

    this.noReportAreas = [];
    this.validAreas = [];
    let lastDate = moment(this.end);
    let validAreaStart = lastDate;
    const { NO_REPORT_TOLERANCE_S } = this.constants();

    const fullTimeStamps = [...(this.events || []), { date: moment(this.start) }];

    // Calculate areas of valid data & no data
    for (const event of fullTimeStamps) {
      const secondsDiff = lastDate.diff(event.date, 'seconds');
      if (secondsDiff > NO_REPORT_TOLERANCE_S) {
        this._pushArea({
          areaArray: this.noReportAreas,
          type: 'no-report',
          dateStart: lastDate,
          dateEnd: event.date,
        });

        this._pushArea({
          areaArray: this.validAreas,
          type: 'valid',
          dateStart: validAreaStart,
          dateEnd: lastDate,
        });
        validAreaStart = event.date;
      }

      lastDate = event.date;
    }

    this._pushArea({
      areaArray: this.validAreas,
      type: 'valid',
      dateStart: validAreaStart,
      dateEnd: lastDate,
    });
  }
}

window.customElements.define('device-monitoring-chart-layer-areas', DeviceMonitoringChartLayerAreas);
